/* eslint-disable no-promise-executor-return */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Navigate, useNavigate } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Input from '../components/ui/input';
import { Button } from '../components/ui/button';
import useAuth from '../stores/useAuth';
import { DASHBOARD_PAGE, LOGIN_PAGE } from '../routes/routeConstant';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Alert, AlertDescription } from '../components/ui/alert';
import { getResetEmail } from '../utils/api';
import getErrorMessage from '../utils/error';

// Form validation schema
const formSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
});

type FormValues = z.infer<typeof formSchema>;

export default function ForgotPassPage() {
  const { isAuthenticated, checkAuth, loading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  // Form submission handler
  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await getResetEmail(data);
      if (response.success) {
        setIsSuccess(true);
        form.reset();
      }
      throw new Error(response.message);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && !isAuthenticated) {
    return (
      <div className="flex w-full h-dvh justify-center items-center">
        <div className="flex animate-pulse aspect-square size-32 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
          <img src="/assets/MSA-Logo.jpg" alt="MSA-Logo" className="rounded" />
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    toast.warning('You are already logged in', {
      position: 'top-right',
    });
    return <Navigate to={DASHBOARD_PAGE} />;
  }

  return (
    <div className="flex justify-center items-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Forgot Password</CardTitle>
          <CardDescription>
            Enter your email address and we&apos;ll send you a link to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isSuccess ? (
            <Alert className="bg-primary/10 border-primary">
              <AlertDescription>
                If an account exists with the email <span className="font-medium">{form.getValues().email}</span>, you
                will receive a password reset link shortly.
              </AlertDescription>
            </Alert>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="your.email@example.com" type="email" autoComplete="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button
            variant="link"
            className="px-0"
            onClick={() => {
              if (isSuccess) {
                setIsSuccess(false);
                form.reset();
              } else {
                navigate(LOGIN_PAGE);
              }
            }}
          >
            {isSuccess ? 'Send another reset link' : 'Back to login'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
